import { Elements, useElements, useStripe } from "@stripe/react-stripe-js"
// The pure entry: the main one adds the js.stripe.com script as a side effect of the import, so
// Stripe would read this page's URL (an onboarding token, or a Discord code and state) at once.
import { loadStripe } from "@stripe/stripe-js/pure"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  type AdminOnboardingBrandingRequest,
  type AdminOnboardingCatalog,
  adminOnboardingBrandingRequestSchema,
  type TrackingPixels,
} from "@ternary/contracts"
import {
  Alert,
  Badge,
  Button,
  Field,
  FormWizard,
  Input,
  Stepper,
  type WizardErrors,
  type WizardStep,
  type WizardStepContext,
} from "@ternary/ui"
import { type ReactElement, type ReactNode, useCallback, useEffect, useRef, useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import {
  AdminApiError,
  completeAdminOnboardingAccount,
  connectAdminOnboarding,
  createAdminOnboardingSetupIntent,
  loadAdminOnboarding,
  loadAdminSession,
  logoutAdmin,
  resumeOnboarding,
  updateAdminOnboardingBranding,
  updateAdminOnboardingPixels,
  verifyAdminOnboarding,
} from "../api.js"
import { brandColorsForSave, COLOR_FORMAT_MESSAGE, ColorField } from "../components/color-field.js"
import { initialStandardLegal, LegalLinks, LINK_MESSAGE } from "../components/legal-links.js"
import { LiveAddress } from "../components/live-address.js"
import { PixelFields, trimPixels, validatePixels } from "../components/pixel-fields.js"
import {
  CardField,
  cardPaymentMethod,
  formatMoney,
  isEmail,
  OrderSummary,
  PASSWORD_MESSAGE,
  passwordErrors,
  SetupPage,
  useErrorsUntilEdited,
} from "../components/setup-page.js"

type StepId = "discord" | "brand" | "tracking" | "account" | "billing" | "payouts"

type OnboardingDraft = TrackingPixels & {
  groupName: string
  brandColor: string
  termsUrl: string
  contactUrl: string
  privacyUrl: string
  standardLegal: boolean
  email: string
  password: string
  confirmPassword: string
}

type Context = WizardStepContext<OnboardingDraft>

const STAGES: readonly AdminOnboardingCatalog["stage"][] = [
  "discord",
  "branding",
  "account",
  "payment",
]

/**
 * Whether admin-api has finished a stage: from the catalog's `steps` (ruling R27), or, on a
 * catalog without them, from the stage order.
 */
function isDone(catalog: AdminOnboardingCatalog, stage: "discord" | "branding" | "account") {
  const status = catalog.steps?.[stage]
  return status ? status === "done" : STAGES.indexOf(catalog.stage) > STAGES.indexOf(stage)
}

/** Set once Tracking is saved or skipped in this tab, so a reload opens the step after it. */
function trackingKey(token: string) {
  return `tny_onboarding_tracking:${token}`
}

function trackingSeen(token: string) {
  try {
    return sessionStorage.getItem(trackingKey(token)) !== null
  } catch {
    // Blocked storage: Tracking shows again, which is harmless.
    return false
  }
}

function markTrackingSeen(token: string) {
  try {
    sessionStorage.setItem(trackingKey(token), "1")
  } catch {
    // Blocked storage: Tracking shows again after a reload, which is harmless.
  }
}

/**
 * Where the wizard opens: the first step still to do. Steps done before it show as done; done
 * steps after it, which only a self-registered tenant has (its account and billing, R27), leave
 * the path, because the stepper can only mark the steps before the current one as done.
 */
function planFor(catalog: AdminOnboardingCatalog, token: string) {
  const todo: [StepId, boolean][] = [
    ["discord", !isDone(catalog, "discord")],
    ["brand", !isDone(catalog, "branding")],
    // Seen in this tab, or recorded by the server from any tab.
    ["tracking", !trackingSeen(token) && catalog.tracking.status === "pending"],
    ["account", !isDone(catalog, "account")],
  ]
  const start = todo.find(([, pending]) => pending)?.[0] ?? "payouts"
  return { start, skipAccount: start !== "payouts" && isDone(catalog, "account") }
}

const PRIVACY_HINT = "Signup pages go live once it is set."

/** An absolute http(s) link that the onboarding branding contract also accepts. */
function isLink(text: string) {
  return (
    (text.startsWith("https://") || text.startsWith("http://")) &&
    adminOnboardingBrandingRequestSchema.shape.termsUrl.safeParse(text).success
  )
}

/** Ternary's standard terms replace both links: only while they are available (G5). */
function usesStandardLegal(draft: OnboardingDraft, standardAvailable: boolean) {
  return standardAvailable && draft.standardLegal
}

function brandErrors(draft: OnboardingDraft, standardAvailable: boolean): WizardErrors {
  const errors: WizardErrors = {}
  const standard = usesStandardLegal(draft, standardAvailable)
  if (!draft.groupName.trim()) errors.groupName = "Enter your company name."
  if (!brandColorsForSave(draft.brandColor)) errors.brandColor = COLOR_FORMAT_MESSAGE
  if (!standard && !isLink(draft.termsUrl.trim())) errors.termsUrl = LINK_MESSAGE
  if (!isLink(draft.contactUrl.trim())) errors.contactUrl = LINK_MESSAGE
  const privacy = draft.privacyUrl.trim()
  if (!standard && privacy && !isLink(privacy)) errors.privacyUrl = LINK_MESSAGE
  return errors
}

/**
 * The branding POST: the text trimmed, the colours from `brandColorsForSave`, no empty privacy link.
 * With the standard terms chosen it sends no link, and admin-api keeps any saved one. `standardLegal`
 * goes only while they are available, so the request is today's while they are off.
 */
function brandingRequest(
  draft: OnboardingDraft,
  standardAvailable: boolean,
): AdminOnboardingBrandingRequest {
  const colors = brandColorsForSave(draft.brandColor)
  if (!colors) throw new Error(COLOR_FORMAT_MESSAGE)
  const privacyUrl = draft.privacyUrl.trim()
  const named = {
    contactUrl: draft.contactUrl.trim(),
    groupName: draft.groupName.trim(),
    ...colors,
  }
  if (usesStandardLegal(draft, standardAvailable)) return { ...named, standardLegal: true }
  return {
    ...named,
    ...(privacyUrl ? { privacyUrl } : {}),
    termsUrl: draft.termsUrl.trim(),
    ...(standardAvailable ? { standardLegal: false } : {}),
  }
}

function accountErrors(draft: OnboardingDraft): WizardErrors {
  const errors = passwordErrors(draft)
  return isEmail(draft.email.trim()) ? errors : { email: "Enter an email address.", ...errors }
}

function pixelsOf(draft: OnboardingDraft): TrackingPixels {
  return { google: draft.google, meta: draft.meta, tiktok: draft.tiktok }
}

/** What the install callback's `?error=` means, in plain words. */
const INSTALL_ERRORS: Record<string, string> = {
  "install-state": "The Discord link expired or was already used. Add the bot again.",
  "install-unconfirmed": "Discord did not confirm the bot. Add it again and approve it.",
  "guild-claimed":
    "That Discord server is already connected to another organization. Add the bot to a different server.",
}

/** A step admin-api has already finished: what is saved, and where it changes later. */
function Saved({ children }: { children: ReactNode }) {
  return (
    <p className="admin-setup-saved">
      <Badge tone="success">Done</Badge> <span>{children}</span>
    </p>
  )
}

function DiscordStep({
  authorizationUrl,
  done,
  error,
  installError,
}: {
  authorizationUrl: string
  done: boolean
  error: string | undefined
  installError: string | null
}) {
  if (done) {
    return (
      <p className="admin-setup-saved">
        <Badge tone="success">Connected</Badge> <span>The bot is in your Discord server.</span>
      </p>
    )
  }
  const failed = installError
    ? (INSTALL_ERRORS[installError] ?? "Discord could not add the bot. Add it again.")
    : null
  const message = error ?? failed
  // A plain link: the install starts at admin-api, which sends the browser on to Discord.
  const install = (
    <a className="tny-button admin-setup-action" href={authorizationUrl}>
      {failed ? "Try again" : "Add the bot"}
    </a>
  )
  return (
    <>
      {/* With a message, the install is its action, so the step keeps one. */}
      {message ? (
        <Alert action={install} tone="danger">
          {message}
        </Alert>
      ) : (
        install
      )}
      <p className="admin-note">
        Discord asks you to choose your server and approve the bot, then brings you back here.
      </p>
    </>
  )
}

function BrandStep({
  draft,
  errors,
  setDraft,
  standardAvailable,
}: Context & { standardAvailable: boolean }) {
  const shown = useErrorsUntilEdited(errors, draft)
  // A link is required, unless it comes with `optionalHint`, which says what leaving it out means.
  const link = (
    key: "termsUrl" | "contactUrl" | "privacyUrl",
    label: string,
    optionalHint?: string,
  ) => (
    <Field
      error={shown[key]}
      hint={shown[key] ? undefined : optionalHint}
      label={label}
      required={!optionalHint}
    >
      <Input
        autoComplete="url"
        inputMode="url"
        onChange={(event) => setDraft({ [key]: event.currentTarget.value })}
        spellCheck={false}
        type="url"
        value={draft[key]}
      />
    </Field>
  )
  // Three groups: who the business is, where members reach it, then its legal links.
  return (
    <div className="admin-brand-step">
      <div className="admin-field-grid">
        <Field error={shown.groupName} label="Company name" required>
          <Input
            autoComplete="organization"
            maxLength={120}
            onChange={(event) => setDraft({ groupName: event.currentTarget.value })}
            value={draft.groupName}
          />
        </Field>
        <ColorField
          error={shown.brandColor}
          label="Brand colour"
          onChange={(brandColor) => setDraft({ brandColor })}
          value={draft.brandColor}
        />
      </div>
      {link("contactUrl", "Contact page")}
      <div className="admin-brand-legal">
        <h3 className="admin-heading">Legal</h3>
        {standardAvailable ? (
          <LegalLinks
            companyName={draft.groupName}
            draft={draft}
            errors={{ privacyUrl: shown.privacyUrl, termsUrl: shown.termsUrl }}
            onChange={setDraft}
            privacyHint={PRIVACY_HINT}
            standardAvailable
          />
        ) : (
          <div className="admin-field-grid">
            {link("termsUrl", "Terms of Service link")}
            {/* Optional here, but customer-api publishes no signup page until it is set. */}
            {link("privacyUrl", "Privacy policy link", PRIVACY_HINT)}
          </div>
        )}
      </div>
    </div>
  )
}

function TrackingStep({ draft, errors, setDraft }: Context) {
  const shown = useErrorsUntilEdited(errors, draft)
  return (
    <>
      <PixelFields errors={shown} onChange={(pixels) => setDraft(pixels)} value={pixelsOf(draft)} />
      <p className="admin-note">
        We send visits, sign-up starts and completed sign-ups to these pixels.
      </p>
      <details className="admin-note admin-event-names">
        <summary>Event names</summary>
        Only after a visitor accepts cookies: <strong>PageView</strong> on every signup page,{" "}
        <strong>Lead</strong> on their details and <strong>CompleteRegistration</strong> when they
        join.
      </details>
    </>
  )
}

function AccountStep({ draft, errors, setDraft }: Context) {
  const shown = useErrorsUntilEdited(errors, draft)
  return (
    <div className="admin-setup-fields">
      <Field error={shown.email} label="Email" required>
        <Input
          autoComplete="email"
          onChange={(event) => setDraft({ email: event.currentTarget.value })}
          spellCheck={false}
          type="email"
          value={draft.email}
        />
      </Field>
      <Field
        error={shown.password}
        hint={shown.password ? undefined : PASSWORD_MESSAGE}
        label="Password"
        required
      >
        <Input
          autoComplete="new-password"
          onChange={(event) => setDraft({ password: event.currentTarget.value })}
          type="password"
          value={draft.password}
        />
      </Field>
      <Field error={shown.confirmPassword} label="Confirm password" required>
        <Input
          autoComplete="new-password"
          onChange={(event) => setDraft({ confirmPassword: event.currentTarget.value })}
          type="password"
          value={draft.confirmPassword}
        />
      </Field>
    </div>
  )
}

function PayoutsStep({
  catalog,
  onError,
  token,
}: {
  catalog: AdminOnboardingCatalog
  onError(error: unknown): void
  token: string
}) {
  const queryClient = useQueryClient()
  const connect = useMutation({
    mutationFn: () => connectAdminOnboarding(token),
    onError,
    async onSuccess(result) {
      window.open(result.url, "_blank", "noopener,noreferrer")
      await queryClient.invalidateQueries({ queryKey: ["admin-onboarding", token] })
    },
  })
  const { connectAvailable, connected } = catalog.payment
  return (
    <>
      {connected ? (
        <p className="admin-setup-saved">
          <Badge tone="success">Connected</Badge> <span>Stripe is connected.</span>
        </p>
      ) : (
        <>
          <Button
            className="admin-setup-action"
            disabled={!connectAvailable}
            loading={connect.isPending}
            onClick={() => connect.mutate()}
            variant="secondary"
          >
            Connect Stripe
          </Button>
          {/* Finish does not wait on Stripe (R28): payouts can be connected any time. */}
          <p className="admin-note">
            {connectAvailable ? "" : "Stripe Connect is not available right now. "}
            You can connect Stripe later under Connections → Payment gateways.
          </p>
        </>
      )}
      {connect.error instanceof Error ? (
        <Alert
          action={
            <Button
              loading={connect.isPending}
              onClick={() => connect.mutate()}
              size="sm"
              variant="secondary"
            >
              Try again
            </Button>
          }
          tone="danger"
        >
          {connect.error.message}
        </Alert>
      ) : null}
      {/* Mounted from the start, so the notice is announced when it appears. */}
      <p className="admin-note" role="status">
        {connect.isSuccess && !connected
          ? "Stripe opened in a new tab. This page updates once your account is connected."
          : ""}
      </p>
    </>
  )
}

/**
 * The end of the wizard, once verify has spent the link: the stepper with every step done, a
 * success Alert, and the business's live address with Copy when registration gave it one. An owner
 * registration already signed in to this organization opens the dashboard; any other owner, or a
 * session of another organization, signs in with the password they just set. Until the session is
 * known, neither button shows.
 */
function DonePanel({
  address,
  organizationId,
  steps,
  title,
}: {
  address: AdminOnboardingCatalog["address"]
  organizationId: string
  steps: { id: string; label: string }[]
  title: string
}) {
  const heading = useRef<HTMLHeadingElement>(null)
  useEffect(() => heading.current?.focus(), [])
  const session = useQuery({ queryFn: loadAdminSession, queryKey: ["admin-session"] })
  const signedIn =
    session.data?.authenticated === true && session.data.session.organization.id === organizationId
  // A session of another organization: the sign-in page would send it to that organization's
  // dashboard, so Sign in signs it out first, as the shell's Sign out does, then opens the page.
  const otherOrganization = session.data?.authenticated === true && !signedIn
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const signOut = useMutation({
    mutationFn: logoutAdmin,
    onSettled() {
      queryClient.clear()
      queryClient.setQueryData(["admin-session"], { authenticated: false })
      navigate("/")
    },
  })
  return (
    <div className="tny-wizard">
      <div className="tny-wizard-header">
        <h1 className="tny-wizard-title">{title}</h1>
        <Stepper completedIds={steps.map((step) => step.id)} label={title} steps={steps} />
      </div>
      <div className="tny-wizard-body">
        {/* No live region: the heading takes focus, so it is read as the wizard leaves. */}
        <Alert role="none" tone="success">
          <h2 className="tny-alert-title admin-done-title" ref={heading} tabIndex={-1}>
            Your organization is ready
          </h2>
          {session.isPending
            ? null
            : signedIn
              ? "Your dashboard shows what is left to set up."
              : "Sign in with the password you just set."}
        </Alert>
        {address ? (
          <div className="admin-done-address">
            <p className="admin-heading">Your address</p>
            <LiveAddress url={address.url} />
          </div>
        ) : null}
        {session.isPending ? null : signedIn ? (
          <Link className="tny-button admin-setup-action" to="/dashboard">
            Open your dashboard
          </Link>
        ) : (
          <Link
            aria-busy={signOut.isPending || undefined}
            className="tny-button admin-setup-action"
            onClick={
              otherOrganization
                ? (event) => {
                    event.preventDefault()
                    if (!signOut.isPending) signOut.mutate()
                  }
                : undefined
            }
            to="/"
          >
            Sign in
          </Link>
        )}
      </div>
    </div>
  )
}

/**
 * A step's submit error as a danger Alert. The API's known messages become plain words with what
 * to do next; any other message keeps its words, with Try again.
 */
function StepError({
  goTo,
  message,
  retry,
}: {
  goTo(stepId: string): void
  message: string
  retry(): void
}) {
  if (message === "Account with email already exists.") {
    return (
      <Alert
        action={
          <>
            <Button onClick={() => goTo("account")} size="sm" variant="secondary">
              Back to Account
            </Button>
            <Link to="/">Sign in</Link>
          </>
        }
        tone="danger"
      >
        That email already has a Ternary account.
      </Alert>
    )
  }
  const tryAgain = (
    <Button onClick={retry} size="sm" variant="secondary">
      Try again
    </Button>
  )
  return (
    <Alert action={tryAgain} tone="danger">
      {message === "Onboarding could not be completed."
        ? "We couldn't finish your setup."
        : message}
    </Alert>
  )
}

/** A 410: verify spent the link (here or in another tab), or it expired. */
function isGone(error: unknown) {
  return error instanceof AdminApiError && error.status === 410
}

/** A 410 that says the link expired with its setup still open, so signing in continues it. */
function isExpired(error: unknown) {
  return isGone(error) && (error as AdminApiError).reason === "expired"
}

function OnboardingWizard({
  catalog,
  complete,
  onComplete,
  onGone,
  token,
}: {
  catalog: AdminOnboardingCatalog
  complete: boolean
  onComplete(): void
  /** A step met a 410: no step can succeed now, so the page shows the unavailable panel. */
  onGone(): void
  token: string
}): ReactElement {
  const queryClient = useQueryClient()
  const stripe = useStripe()
  const elements = useElements()
  const [searchParams] = useSearchParams()
  const [plan] = useState(() => planFor(catalog, token))
  const accountSkip = plan.skipAccount ? { skip: () => true } : {}
  const { standardAvailable } = catalog.legal
  const discordDone = isDone(catalog, "discord")
  const brandDone = isDone(catalog, "branding")
  const accountDone = isDone(catalog, "account")
  // Only the account stage takes a card: the endpoint answers 409 at every other stage.
  const setupQuery = {
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      createAdminOnboardingSetupIntent(token, { signal }),
    queryKey: ["admin-onboarding-setup-intent", token],
    retry: false,
  }
  const setup = useQuery({ ...setupQuery, enabled: catalog.stage === "account" })
  const setupGone = isGone(setup.error)
  useEffect(() => {
    if (setupGone) onGone()
  }, [setupGone, onGone])
  const amount = formatMoney(catalog.billing.amountMinor, catalog.billing.currency)
  const title = catalog.branding.groupName
    ? `Set up ${catalog.branding.groupName}`
    : "Set up your organization"

  const save = (result: AdminOnboardingCatalog) =>
    queryClient.setQueryData(["admin-onboarding", token], result)

  async function finish() {
    await verifyAdminOnboarding(token)
    onComplete()
  }

  async function pay(draft: OnboardingDraft) {
    // The setup loaded, or the one still loading, such as the fresh one after a refused payment.
    const intent = await queryClient.fetchQuery(setupQuery)
    const paymentMethodId = await cardPaymentMethod(intent, stripe, elements)
    try {
      save(
        await completeAdminOnboardingAccount(token, {
          confirmPassword: draft.confirmPassword,
          email: draft.email.trim(),
          password: draft.password,
          paymentMethodId,
        }),
      )
    } catch (error) {
      // Stripe confirms a setup intent once, so a retry needs a fresh one.
      if (!intent.fixturePaymentMethodId) void setup.refetch()
      throw error
    }
  }

  /** Rethrows a step's error for the wizard to show, after handing a 410 to the page. */
  function stepFailed(error: unknown): never {
    if (isGone(error)) onGone()
    throw error
  }

  async function submitStep(stepId: string, draft: OnboardingDraft) {
    try {
      if (stepId === "brand" && !brandDone) {
        save(await updateAdminOnboardingBranding(token, brandingRequest(draft, standardAvailable)))
      } else if (stepId === "tracking") {
        save(await updateAdminOnboardingPixels(token, trimPixels(pixelsOf(draft))))
        markTrackingSeen(token)
      } else if (stepId === "billing" && !accountDone) {
        await pay(draft)
      }
    } catch (error) {
      stepFailed(error)
    }
  }

  function skipStep(stepId: string) {
    if (stepId === "tracking") markTrackingSeen(token)
  }

  /**
   * The card setup's hold on Billing: only a setup that failed to load, which is asked for again.
   * One still loading holds nothing, because Pay waits for it.
   */
  function cardErrors(): WizardErrors {
    if (accountDone || !setup.isError || setup.isFetching) return {}
    void setup.refetch()
    return { card: setup.error instanceof Error ? setup.error.message : "Card entry failed." }
  }

  const steps: WizardStep<OnboardingDraft>[] = [
    {
      id: "discord",
      title: "Discord",
      description:
        "Add the Ternary bot to your Discord server, so it can give members their roles.",
      // The bot install is the step's one primary action until the bot is in.
      nextVariant: discordDone ? undefined : "secondary",
      validate: () =>
        discordDone ? {} : { discord: "Add the bot to your Discord server to continue." },
      render: ({ errors }) => (
        <DiscordStep
          authorizationUrl={catalog.discord.authorizationUrl}
          done={discordDone}
          error={errors.discord}
          installError={searchParams.get("error")}
        />
      ),
    },
    {
      id: "brand",
      title: "Brand",
      validate: (draft) => (brandDone ? {} : brandErrors(draft, standardAvailable)),
      render: (context) =>
        brandDone ? (
          <Saved>Your brand is saved. Change it later under Settings → Branding.</Saved>
        ) : (
          <BrandStep {...context} standardAvailable={standardAvailable} />
        ),
    },
    {
      id: "tracking",
      title: "Tracking",
      description: "Optional. You can change these later under Connections → Tracking pixels.",
      fields: ["meta", "google", "tiktok"],
      nextLabel: "Save and continue",
      // Once the server holds a save, a skip would restore the IDs the wizard opened with.
      skipLabel: catalog.tracking.status === "pending" ? "Add later" : undefined,
      validate: (draft) => validatePixels(pixelsOf(draft)),
      render: (context) => <TrackingStep {...context} />,
    },
    {
      id: "account",
      title: "Account",
      description: "The owner login for your dashboard.",
      ...accountSkip,
      validate: (draft) => (accountDone ? {} : accountErrors(draft)),
      render: (context) =>
        accountDone ? <Saved>Your owner account is set up.</Saved> : <AccountStep {...context} />,
    },
    {
      id: "billing",
      title: "Billing",
      // Its submit sends the account step's values, so a change there sends it again.
      fields: ["email", "password", "confirmPassword"],
      nextLabel: accountDone ? undefined : `Save card and pay ${amount}`,
      ...accountSkip,
      validate: cardErrors,
      render: ({ errors }) =>
        accountDone ? (
          <Saved>Your card is saved and your plan is paid.</Saved>
        ) : (
          <>
            <OrderSummary rows={[["Ternary Business License", amount]]} total={["Total", amount]} />
            <CardField error={errors.card} publishableKey={catalog.billing.publishableKey} />
          </>
        ),
    },
    {
      id: "payouts",
      title: "Payouts",
      description: "Connect Stripe to sell paid products. Free products work without it.",
      fields: [],
      render: () => (
        <PayoutsStep
          catalog={catalog}
          onError={(error) => {
            if (isGone(error)) onGone()
          }}
          token={token}
        />
      ),
    },
  ]

  if (complete) {
    return (
      <DonePanel
        address={catalog.address}
        organizationId={catalog.organizationId}
        steps={steps
          .filter((step) => !step.skip)
          .map((step) => ({ id: step.id, label: step.title }))}
        title={title}
      />
    )
  }

  return (
    <FormWizard
      initialDraft={{
        brandColor: catalog.branding.primaryColor,
        confirmPassword: "",
        contactUrl: catalog.branding.contactUrl,
        email: "",
        groupName: catalog.branding.groupName,
        password: "",
        privacyUrl: catalog.branding.privacyUrl,
        standardLegal: initialStandardLegal(catalog.branding, standardAvailable),
        termsUrl: catalog.branding.termsUrl,
        ...catalog.pixels,
      }}
      initialStepTrusted
      onSkip={skipStep}
      onStepSubmit={submitStep}
      renderError={(message, actions) => <StepError message={message} {...actions} />}
      // Finish, on Payouts, verifies and shows the done panel. Verify no longer waits on Stripe
      // (R28), so it finishes with or without payouts.
      onSubmit={() => finish().catch(stepFailed)}
      stepId={plan.start}
      steps={steps}
      submitLabel="Finish"
      title={title}
    />
  )
}

/**
 * The page for a link that no longer works, with a way to sign in. After a step met a 410 its
 * heading takes focus, because it replaces the wizard the focus was in.
 */
function Unavailable({
  focus = false,
  message,
  resumable = false,
}: {
  focus?: boolean
  message: string
  /** The link expired with setup still open, so signing in continues it. */
  resumable?: boolean
}) {
  const heading = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    if (focus) heading.current?.focus()
  }, [focus])
  // An owner or admin already signed in gets a fresh link here, from the same route as the
  // dashboard's Continue setup (G7). Anyone else signs in first: the dashboard offers it there.
  const session = useQuery({ queryFn: loadAdminSession, queryKey: ["admin-session"], retry: false })
  const role = session.data?.authenticated ? session.data.session.account.role : null
  const resume = useMutation({
    mutationFn: resumeOnboarding,
    // A full load, so the new link opens with none of this page's state.
    onSuccess: ({ onboardingUrl }) => window.location.assign(onboardingUrl),
  })
  return (
    <div className="admin-setup-panel">
      <h1 className="tny-wizard-title" ref={heading} tabIndex={-1}>
        Setup link unavailable
      </h1>
      <Alert action={<Link to="/">Go to sign in</Link>} tone="danger">
        {message}
      </Alert>
      {resumable ? (
        <p className="admin-note">Sign in to continue your setup.</p>
      ) : (
        <p className="admin-note">
          Sign in to get a new setup link, or to go on if you already finished.
        </p>
      )}
      {role === "owner" || role === "admin" ? (
        <div className="admin-setup-resume">
          <Button loading={resume.isPending} onClick={() => resume.mutate()}>
            Get a new setup link
          </Button>
          {resume.isError ? (
            <p className="tny-error" role="alert">
              {resume.error instanceof AdminApiError
                ? resume.error.message
                : "Setup could not reach the server. Check your connection, then retry."}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** The setup could not load for a reason other than its link (a network or server failure). */
function LoadFailed({ onRetry, retrying }: { onRetry(): void; retrying: boolean }) {
  return (
    <div className="admin-setup-panel">
      <h1 className="tny-wizard-title">Set up your organization</h1>
      <Alert
        action={
          <Button loading={retrying} onClick={onRetry} size="sm" variant="secondary">
            Try again
          </Button>
        }
        tone="danger"
      >
        We couldn't load your setup. Check your connection and try again.
      </Alert>
    </div>
  )
}

/** A 404 or a 410: the link is unknown, spent or expired. Any other failure is worth a retry. */
function isLinkGone(error: unknown) {
  return error instanceof AdminApiError && (error.status === 404 || error.status === 410)
}

export function AdminOnboardingPage() {
  const token = useParams().token ?? ""
  // Set once verify succeeds. The link is spent then, so the catalog is not fetched again.
  const [complete, setComplete] = useState(false)
  // Set once a step meets a 410, which no later step can get past.
  const [gone, setGone] = useState(false)
  const markGone = useCallback(() => setGone(true), [])
  const onboarding = useQuery({
    enabled: Boolean(token) && !complete && !gone,
    queryFn: ({ signal }) => loadAdminOnboarding(token, { signal }),
    queryKey: ["admin-onboarding", token],
    // While Stripe Connect may be open in another tab, so Payouts shows it connected.
    refetchInterval(query) {
      const catalog = query.state.data
      return catalog?.stage === "payment" && !catalog.payment.connected ? 3000 : false
    },
  })
  // Stripe.js loads only at the account stage, the one that takes a card: it reads the page URL,
  // which carries the token. Once loaded it stays, because Elements keeps its first stripe.
  const cardKey =
    onboarding.data?.stage === "account" ? onboarding.data.billing.publishableKey : null
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null)
  useEffect(() => {
    if (cardKey && !stripePromise) setStripePromise(loadStripe(cardKey))
  }, [cardKey, stripePromise])

  let content: ReactNode
  if (gone || (onboarding.data && isGone(onboarding.error))) {
    content = <Unavailable focus message="This setup link has already been used." />
  } else if (onboarding.data) {
    // Any other failed refetch keeps the wizard and what is typed in it.
    content = (
      <Elements stripe={stripePromise}>
        <OnboardingWizard
          catalog={onboarding.data}
          complete={complete}
          onComplete={() => setComplete(true)}
          onGone={markGone}
          token={token}
        />
      </Elements>
    )
  } else if (onboarding.isError && !isLinkGone(onboarding.error)) {
    // The API's own words never show: they are not the owner's to act on.
    content = (
      <LoadFailed onRetry={() => void onboarding.refetch()} retrying={onboarding.isFetching} />
    )
  } else if (onboarding.isError || !token) {
    content = (
      <Unavailable
        message="This onboarding link is invalid or has expired."
        // Only a link the API says expired, with its setup still open, is continued by signing
        // in. A spent or unknown link (a finished setup's, a reissue's old one, or one spent by
        // an install) keeps the note as it was.
        resumable={isExpired(onboarding.error)}
      />
    )
  } else {
    content = (
      <p aria-busy="true" className="admin-note" role="status">
        Loading your setup…
      </p>
    )
  }
  return <SetupPage>{content}</SetupPage>
}
